import { rem } from 'polished'
import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'

import TopContent from './Common/TopContent'
import { ItemCardVariant } from './ItemCard'
import { SAVE_CURATED_TRIP_BUTTON_CLASS } from '../Buttons/SaveCuratedTripItemButton'
import TripItemSummary from '../TripModal/TripItems/Summary'

import TextButton from 'components/Luxkit/Button/TextButton'
import LineArrowRightIcon from 'components/Luxkit/Icons/line/LineArrowRightIcon'
import StructureLabel from 'components/Luxkit/Label/StructureLabel'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import TextLink from 'components/Luxkit/TextLink'
import BodyText from 'components/Luxkit/Typography/BodyText'
import PrintOnly from 'components/utils/PrintOnly'
import noop from 'lib/function/noop'
import SaveTripItemButton from 'tripPlanner/components/Buttons/SaveTripItemButton'
import BaseItemCard from 'tripPlanner/components/ItemCard/BaseItemCard'
import CardContainer from 'tripPlanner/components/ItemCard/Common/CardContainer'
import ItemLocationSummary from 'tripPlanner/components/ItemCard/Common/ItemLocationSummary'
import TripContext from 'tripPlanner/contexts/TripContext'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { ItemMetadata } from 'tripPlanner/models'
import { TransportItem } from 'tripPlanner/types/tripItem'

interface Props {
  item: TransportItem
  onClickTripName?: () => void
  variant: ItemCardVariant
}

const LocalContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  align-items: baseline;
  justify-content: space-between;
  width: 100%;
  height: 100%;

  & > .${SAVE_CURATED_TRIP_BUTTON_CLASS} {
    align-self: end;
  }
`

const Mid = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.palette.neutral.default.four};
`

const Dates = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(16)};
`

function TransportItemCard({ item, onClickTripName = noop, variant }: Props) {
  const { type, startPlace, endPlace, confirmationCode } = item

  const title = `${ItemMetadata[type].singularName} ${
    item.operatorName ? `• ${item.operatorName}` : ''
  }`
  const tripContext = useContext(TripContext)

  const openModal = useOpenModal()

  const showDetail = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      openModal(TripItemSummary, { item })
      e.stopPropagation()
    },
    [item, openModal],
  )

  return (
    <BaseItemCard item={item}>
      <CardContainer>
        <LocalContainer>
          <TopContent item={item} variant={variant}>
            {title}
          </TopContent>
          {confirmationCode && (
            <PrintOnly>
              <BodyText variant="medium">
                <BodyText as="span" variant="medium" weight="bold">
                  Confirmation number:{' '}
                </BodyText>
                {confirmationCode}
              </BodyText>
            </PrintOnly>
          )}
          <Dates>
            <ItemLocationSummary
              place={startPlace}
              date={item.startDate}
              time={item.startTime}
              day={item.startDay}
              canShowWarning
              item={item}
              variant={variant}
            />
            <Mid>
              <LineArrowRightIcon />
            </Mid>
            <ItemLocationSummary
              place={endPlace}
              date={item.endDate}
              time={item.endTime}
              day={item.endDay}
              item={item}
              variant={variant}
            />
          </Dates>
          {variant === 'drawer' && tripContext && (
            <div>
              <StructureLabel variant="information">
                <BodyTextBlock variant="small">Added to </BodyTextBlock>
                <TextLink
                  size="caption"
                  onClick={(e) => {
                    e.stopPropagation()
                    onClickTripName()
                  }}
                >
                  {tripContext.name}
                </TextLink>
              </StructureLabel>
            </div>
          )}
          <SaveTripItemButton
            className={SAVE_CURATED_TRIP_BUTTON_CLASS}
            item={item}
          />
          <div>
            <TextButton
              kind="tertiary"
              size="small"
              variant="default"
              outdent="left"
              nonInteractable
              onClick={showDetail}
            >
              View details
            </TextButton>
          </div>
        </LocalContainer>
      </CardContainer>
    </BaseItemCard>
  )
}

export default TransportItemCard
